import { Box, Button, Checkbox, Group, Paper, SimpleGrid, Stack, Text, Textarea, TextInput, ThemeIcon, Tooltip } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useTranslation } from 'react-i18next'
import { IconCopy, IconTicket, IconUsers } from '@tabler/icons-react'
import type { Price } from 'api/dto/price'
import type { AdditionalVisitor, RegisteredVisitor } from '../types'
import { inputStyles, textareaStyles } from '../styles'
import { TicketCard } from './ticket-card'
import { AddonCard } from './addon-card'

interface AdditionalAttendeeSectionProps {
  form: UseFormReturnType<RegisteredVisitor, (values: RegisteredVisitor) => RegisteredVisitor>
  isSmallDevice: boolean
  hasFoodIncluded: boolean
  visitor: AdditionalVisitor
  index: number
  prices: Price[]
  addons: Price[]
  onAddonToggle: (index: number, addonId: string) => void
}

export function AdditionalAttendeeSection({ form, isSmallDevice, hasFoodIncluded, visitor, index, prices, addons, onAddonToggle }: AdditionalAttendeeSectionProps) {
  const { t } = useTranslation()

  const copyCompanyInfo = () => {
    form.setFieldValue(`additionalVisitors.${index}.companyName`, form.values.companyName || '')
    form.setFieldValue(`additionalVisitors.${index}.companyPhone`, form.values.companyPhone || '')
    form.setFieldValue(`additionalVisitors.${index}.address.street`, form.values.address?.street || '')
    form.setFieldValue(`additionalVisitors.${index}.address.city`, form.values.address?.city || '')
    form.setFieldValue(`additionalVisitors.${index}.address.province`, form.values.address?.province || '')
    form.setFieldValue(`additionalVisitors.${index}.address.postalCode`, form.values.address?.postalCode || '')
    form.setFieldValue(`additionalVisitors.${index}.address.country`, form.values.address?.country || '')
  }

  return (
    <Paper p={isSmallDevice ? 'md' : 'xl'} radius="md" sx={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <Group position="apart" mb={16}>
        <Group spacing="sm">
          <ThemeIcon size={28} radius="md" variant="light" color="gray">
            <IconUsers size={16} />
          </ThemeIcon>
          <Text weight={600} size="lg" color="#111827">
            {t('register.attendee-number', { number: index + 2 })}
          </Text>
        </Group>
        <Tooltip label={t('register.copy-company-info')}>
          <Button size="xs" variant="light" color="gray" leftIcon={<IconCopy size={14} />} onClick={copyCompanyInfo}>
            {t('register.copy-company-info')}
          </Button>
        </Tooltip>
      </Group>

      {/* Ticket Selection for this attendee */}
      {prices.length > 0 && (
        <Box mb={16} p="md" sx={{ background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
          <Text size="sm" weight={600} color="#374151" mb={10}>
            <IconTicket size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {t('register.ticket-type')}
          </Text>
          <Stack spacing={8}>
            {prices.map((price) => (
              <TicketCard
                key={price.id}
                price={price}
                isSelected={visitor.price === price.id}
                onSelect={() => form.setFieldValue(`additionalVisitors.${index}.price`, price.id)}
                t={t}
              />
            ))}
          </Stack>

          {/* Addons for this attendee */}
          {addons.length > 0 && (
            <Box mt={12}>
              <Text size="sm" weight={600} color="#374151" mb={8}>
                {t('register.addons')}
              </Text>
              <Stack spacing={6}>
                {addons.map((addon) => (
                  <AddonCard key={addon.id} addon={addon} isSelected={visitor.addons?.includes(addon.id) || false} onToggle={() => onAddonToggle(index, addon.id)} t={t} />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Personal Info */}
      <Stack spacing={10}>
        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={10}>
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.firstName')}
            placeholder={t('visitor.firstName')}
            {...form.getInputProps(`additionalVisitors.${index}.firstName`)}
          />
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.lastName')}
            placeholder={t('visitor.lastName')}
            {...form.getInputProps(`additionalVisitors.${index}.lastName`)}
          />
        </SimpleGrid>

        <TextInput
          withAsterisk
          sx={inputStyles}
          type="email"
          label={t('visitor.email')}
          placeholder={t('visitor.email')}
          {...form.getInputProps(`additionalVisitors.${index}.email`)}
        />

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={10}>
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.company')}
            placeholder={t('visitor.company')}
            {...form.getInputProps(`additionalVisitors.${index}.companyName`)}
          />
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.company-phone')}
            placeholder={t('visitor.company-phone')}
            {...form.getInputProps(`additionalVisitors.${index}.companyPhone`)}
          />
        </SimpleGrid>

        <TextInput
          withAsterisk
          sx={inputStyles}
          label={t('visitor.street')}
          placeholder={t('visitor.street')}
          {...form.getInputProps(`additionalVisitors.${index}.address.street`)}
        />

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={10}>
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.city')} placeholder={t('visitor.city')} {...form.getInputProps(`additionalVisitors.${index}.address.city`)} />
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.province')}
            placeholder={t('visitor.province')}
            {...form.getInputProps(`additionalVisitors.${index}.address.province`)}
          />
        </SimpleGrid>

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={10}>
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.postal-code')}
            placeholder={t('visitor.postal-code')}
            {...form.getInputProps(`additionalVisitors.${index}.address.postalCode`)}
          />
          <TextInput
            withAsterisk
            sx={inputStyles}
            label={t('visitor.country')}
            placeholder={t('visitor.country')}
            {...form.getInputProps(`additionalVisitors.${index}.address.country`)}
          />
        </SimpleGrid>

        <TextInput sx={inputStyles} label={t('visitor.job-title')} placeholder={t('visitor.job-title')} {...form.getInputProps(`additionalVisitors.${index}.jobTitle`)} />

        {hasFoodIncluded && (
          <Textarea
            sx={textareaStyles}
            label={t('visitor.dietary-restrictions')}
            placeholder={t('visitor.dietary-restrictions')}
            minRows={2}
            {...form.getInputProps(`additionalVisitors.${index}.dietaryRestrictions`)}
          />
        )}

        <Checkbox
          mt={4}
          size="sm"
          color="red"
          styles={{
            input: { '&:checked': { background: '#850014', borderColor: '#850014' } },
            label: { fontSize: 13, color: '#4b5563' },
          }}
          label={t('visitor.consent')}
          {...form.getInputProps(`additionalVisitors.${index}.hasConsent`, { type: 'checkbox' })}
        />
      </Stack>
    </Paper>
  )
}
